import { Shield, Loader2, AlertCircle } from 'lucide-react';
import { motion } from 'motion/react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/components/ui/toast';
import { useAdminTeams, useAdminCheck } from '@/hooks/useAdmin';

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function PlanBadge({ plan }: { plan: string }) {
  const variant = plan === 'pro' ? 'default' : plan === 'enterprise' ? 'default' : 'outline';
  return (
    <Badge variant={variant} className="capitalize">
      {plan}
    </Badge>
  );
}

export function AdminPage() {
  const toast = useToast();
  const { isAdmin, isLoading: isCheckingAdmin } = useAdminCheck();
  const { teams, isLoading, error, toggleBillingExempt, isUpdating } = useAdminTeams();

  const handleToggleExempt = async (teamId: string, currentValue: boolean) => {
    try {
      await toggleBillingExempt(teamId, !currentValue);
      toast.success(`Billing exempt ${!currentValue ? 'enabled' : 'disabled'}`);
    } catch (err) {
      console.error('Failed to toggle billing exempt:', err);
      toast.error('Failed to update team');
    }
  };

  if (isCheckingAdmin || isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-2 text-destructive">
          <AlertCircle className="h-5 w-5" />
          <span>Access denied. Admin privileges required.</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-2 text-destructive">
          <AlertCircle className="h-5 w-5" />
          <span>Failed to load admin data</span>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="p-6 space-y-6 max-w-6xl mx-auto"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-primary/10">
          <Shield className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold">Admin</h1>
          <p className="text-muted-foreground">Manage all teams and billing</p>
        </div>
      </div>

      {/* Teams Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Teams</CardTitle>
          <CardDescription>
            {teams.length} team{teams.length !== 1 ? 's' : ''} total
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Billing Exempt</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {teams.map((team) => (
                <TableRow key={team.id}>
                  <TableCell className="font-medium">{team.name}</TableCell>
                  <TableCell>
                    <PlanBadge plan={team.plan} />
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">
                      {team.subscription_status || 'inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(team.created_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Switch
                      checked={team.billing_exempt}
                      onCheckedChange={() => handleToggleExempt(team.id, team.billing_exempt)}
                      disabled={isUpdating}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {teams.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    No teams found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </motion.div>
  );
}
